import qs from 'qs';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useDealer } from '~/entity/Dealer';
import { useDealersForSelect } from '~/entity/Dealers/hooks/useDealersForSelect';
import {
  FilterFieldOption,
  FilterFieldOptionsHookResult,
} from '~/features/filter';

/**
 * Адаптер для подключения списка дилеров в качестве опций фильтра.
 *
 * Делает три вещи поверх `useDealersForSelect`:
 *  1. Приводит контракт хука к `FilterFieldOptionsHookResult`.
 *  2. Нормализует `value` опции к строке (URL хранит строку).
 *  3. Если в URL уже выбраны `dealerIds`, подгружает этих дилеров по id
 *     и инжектит их в options, чтобы Select мог отобразить лейблы
 *     до/без полной подгрузки списка.
 */
export const useDealerOptions = (): FilterFieldOptionsHookResult => {
  const { options, loading, onScrollBottom, searchDealers } = useDealersForSelect();
  const { getById } = useDealer();
  const location = useLocation();

  // В URL мультивыбор хранится строкой через запятую.
  const dealerIdsFromUrl = useMemo(() => {
    const parsed = qs.parse(location.search.slice(1)) as Record<string, unknown>;
    const raw = parsed?.dealerIds;
    if (raw == null || raw === '') return [];
    const str = Array.isArray(raw) ? raw.map(String).join(',') : String(raw);
    return str
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }, [location.search]);

  const [preloadedOptions, setPreloadedOptions] = useState<FilterFieldOption[]>([]);

  // Нормализуем value опций к строке, чтобы корректно сравнивать с URL/формой.
  const normalizedOptions = useMemo<FilterFieldOption[]>(
    () => options.map((o) => ({ label: o.label, value: String(o.value) })),
    [options],
  );

  useEffect(() => {
    if (!dealerIdsFromUrl.length) {
      setPreloadedOptions([]);
      return;
    }
    // Подгружаем только тех выбранных дилеров, которых нет в списке.
    const missing = dealerIdsFromUrl.filter(
      (id) => !normalizedOptions.some((o) => o.value === id),
    );
    if (!missing.length) return;

    let cancelled = false;

    Promise.all(
      missing.map((id) => {
        const params = new URLSearchParams();
        params.append('id', id);

        return getById(params.toString())
          .then((res) => {
            const data = res?.data;
            if (!data || data.id == null) return null;
            return { label: data.dealerName, value: String(data.id) };
          })
          .catch(() => {
            /* молча игнорируем — Select покажет сырой id */
            return null;
          });
      }),
    ).then((loaded) => {
      if (cancelled) return;
      const fresh = loaded.filter(
        (o): o is FilterFieldOption => o != null,
      );
      if (!fresh.length) return;
      setPreloadedOptions((prev) => {
        const merged = [...prev];
        fresh.forEach((opt) => {
          if (!merged.some((o) => o.value === opt.value)) merged.push(opt);
        });
        return merged;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [dealerIdsFromUrl, normalizedOptions, getById]);

  const mergedOptions = useMemo<FilterFieldOption[]>(() => {
    // Подгруженные опции держим только пока они выбраны в URL.
    const active = preloadedOptions.filter(
      (o) =>
        dealerIdsFromUrl.includes(String(o.value)) &&
        !normalizedOptions.some((n) => n.value === o.value),
    );
    return [...active, ...normalizedOptions];
  }, [normalizedOptions, preloadedOptions, dealerIdsFromUrl]);

  return {
    options: mergedOptions,
    loading,
    onScrollBottom,
    searchOptions: searchDealers,
  };
};
